import type { Account } from "@tandemdiabetes/sugarmate-api-react-client"
import { Trend } from "@tandemdiabetes/sugarmate-api-react-client"
import dayjs from "dayjs"
import { useMemo } from "react"

import styles from "./Reading.module.scss"
import { TrendIcon } from "src/components"
import {
  usePolyglot,
  useFollowedUsers,
} from "src/contexts"
import {
  useFirstGlucoseReading,
  useReadableGlucoseUnit,
  useReadableGlucoseValue,
  useReadingDelta,
  useThisMinute,
} from "src/hooks"
import type { Reading } from "src/types"

interface Props {
  accountId: Account["id"];
  userName: string;
  reading: Reading;
}

export function Reading(props: Props) {
  const {
    accountId,
    userName,
    reading,
  } = props

  const { currentDeviceSettings } = useFollowedUsers()
  const polyglot = usePolyglot()
  const thisMinute = useThisMinute()
  const previousGlucoseReading = useFirstGlucoseReading(
    accountId,
    reading.startedAt,
  )
  const unit = useReadableGlucoseUnit()
  const value = useReadableGlucoseValue(reading.glucose.mg_dl)

  const minutesAgo = useMemo(
    () => dayjs(thisMinute).diff(
      reading.startedAt,
      "minute",
    ),
    [
      reading,
      thisMinute,
    ],
  )

  const readingDelta = useReadingDelta(
    reading,
    previousGlucoseReading,
  )

  const showDelta = currentDeviceSettings.show_delta

  const minutesAgoMessage = polyglot.t(
    "pages.home.user_header.users_data_min_ago",
    {
      smart_count: minutesAgo,
      user: userName,
    },
  )

  return (
    <>
      <div className={styles.readingContainer}>
        <div
          className={styles.reading}
          id="cgm-reading-value"
        >
          {value}
        </div>
        <TrendIcon
          className={styles.trendIcon}
          trend={reading.glucose.trend ?? Trend.None}
          id="cgm-trend-arrow"
        />
        {showDelta && (
          <div className={styles.deltaContainer}>
            <div
              className={styles.delta}
              id="cgm-reading-delta"
            >
              {readingDelta}
            </div>
            <div
              className={styles.units}
              id="cgm-unit"
            >
              {unit}
            </div>
          </div>
        )}
      </div>
      <div
        className={styles.info}
        id="cgm-reading-time"
      >
        {minutesAgoMessage}
      </div>
    </>
  )
}
